<template>
	<TheBox :title="'Endgültige Entscheidung:'">
		<template #boxheaderRight>
			<TheButton v-if="isEditing" @buttonclick="stopEditing" type="default">Bearbeitung abbrechen</TheButton>
		</template>

		<TheForm :model="request" :rules="rules" :waiting="waiting" ref="formRef" :id="id" :scrollToError="false">
			<el-form-item prop="state" class="el-row">
				<el-col :sm="12" :xs="24">
					<el-select v-model="request.state" placeholder="Bitte wählen" :disabled="suggestionLoading">
						<el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-col>
			</el-form-item>

			<el-row class="flex flex--alignstart werberatDecisionForm__text">
				<el-col :xs="24" :sm="12">
					<h3>Begründung</h3>
				</el-col>

				<el-col :xs="24" :sm="12" class="flex flex--justifyend">
					<el-tooltip
						effect="dark"
						:content="'Sie können sich erst Text generieren lassen, wenn genug Entscheidungen vorhanden sind.'"
						placement="top"
						:disabled="existingDecisions.length > 3"
					>
						<TheButton @buttonclick="getSuggestion" :disabled="existingDecisions.length <= 3" :loading="suggestionLoading">KI-Vorschlag</TheButton>
					</el-tooltip>
				</el-col>
			</el-row>
			<el-form-item label="" prop="text">
				<el-input
					v-model="request.text"
					:autosize="{ minRows: 10 }"
					type="textarea"
					:disabled="suggestionLoading"
					:placeholder="
						suggestionLoading
							? 'Bitte warten Sie, während der KI-Vorschlag generiert wird.'
							: 'Schreiben Sie hier Ihre Begründung oder nutzen Sie „KI-Vorschlag“ um einen Vorschlag erstellen zu lassen, den Sie editieren können.'
					"
				/>
			</el-form-item>

			<el-alert :title="errorMsg" type="error" v-if="error" />

			<TheButton type="danger" @buttonclick="triggerDialog" :disabled="waiting">Entscheidung treffen</TheButton>
		</TheForm>
	</TheBox>

	<el-dialog
		v-model="openDialogConfirmFinalDecision"
		:title="'Endgültige Entscheidung treffen'"
		id="dialogConfirmFinalDecision"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<div class="has-status" :class="`has-status--red`">
			{{ request.state?.label }}
		</div>

		<el-alert type="info" :closable="false" effect="dark" show-icon>
			<template #title>
				<p><strong>Wichtig:</strong></p>
				<p>
					Mit dieser Entscheidung bestätigen Sie, dass Sie gemäß der Mehrheit der Werberäte handeln und die Regeln des Werberats sowie den Ethik-Kodex
					einhalten.
				</p>
			</template>
		</el-alert>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="saveDecision" :disabled="waiting" :loading="waiting">Entscheidung treffen</TheButton>
				<TheButton type="default" @buttonclick="openDialogConfirmFinalDecision = false">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import TheForm from '../TheForm.vue';
import TheButton from '../TheButton.vue';
import TheBox from '../TheBox.vue';
import { texts } from '../../utils/texts';
import { defineComponent, ref } from 'vue';
import { returnFormOptions } from '@/utils/formOptions';
import { validateForm, resetForm, dialogBeforeClose } from '@/utils/functions';
import type { FormInstance } from 'element-plus';
import { sendCase, caseFinalDecision } from '../../requests/cases';
import { CaseState, FinalDecisionRequest, DecisionState } from '../../types/types';
import { prepareSearchRequest } from '@/utils/search/searchUtils';
import { createAISuggestion } from '@/requests/decisions';

interface BoxWerberatDecisionData {
	request: {
		state: CaseState | null;
		text: string;
	};
	rules: Object;
	id: string;
	waiting: boolean;
	error: boolean;
	errorMsg: string;
	suggestionLoading: boolean;
	DecisionState: typeof DecisionState;
	openDialogConfirmFinalDecision: boolean;
}

export default defineComponent({
	name: 'BoxWerberatDecision',
	data(): BoxWerberatDecisionData {
		return {
			request: {
				state: null,
				text: '',
			},
			rules: {
				state: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				text: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
			},
			id: 'werberatDecisionForm',
			waiting: false,
			error: false,
			errorMsg: texts.cases.saveFailed,
			suggestionLoading: false,
			DecisionState,
			openDialogConfirmFinalDecision: false,
		};
	},
	components: {
		TheBox,
		TheForm,
		TheButton,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	props: {
		caseId: {
			type: String,
			required: true,
		},
		existingDecisions: {
			type: Array,
			default: () => [],
		},
		isEditing: {
			type: Boolean,
			default: false,
		},
		currentFinalDecision: {
			type: Object as () => CaseState | null,
			default: null,
		},
		currentReasonText: {
			type: String,
			default: '',
		},
	},
	emits: ['reload', 'stopEditing'],
	computed: {
		statusOptions() {
			return returnFormOptions('caseStateDecisionsOptions', false, false);
		},
		texts() {
			return texts;
		},
	},
	methods: {
		async triggerDialog() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.openDialogConfirmFinalDecision = true;
		},
		async saveDecision() {
			this.waiting = true;

			let searchRequest = prepareSearchRequest(this.request);

			const requestPreliminaryDecision = {
				caseId: String(this.caseId),
				...searchRequest,
			} as FinalDecisionRequest;

			const result = await caseFinalDecision(requestPreliminaryDecision);
			if (result.success) {
				this.handleSuccess();
			} else {
				this.error = true;
			}

			this.openDialogConfirmFinalDecision = false;
			this.waiting = false;
		},
		handleSuccess() {
			resetForm(this.formRef, this.id);
			this.error = false;

			this.$emit('stopEditing');
			this.$emit('reload');
		},
		async getSuggestion() {
			this.suggestionLoading = true;
			const AIResult = await createAISuggestion({
				caseId: this.caseId,
			});

			if (AIResult.success) {
				this.request.text = AIResult.data.response;
			}

			this.suggestionLoading = false;
		},
		stopEditing() {
			this.$emit('stopEditing');
		},
		dialogBeforeClose,
	},
	mounted() {
		if (this.isEditing) {
			this.request.state = this.statusOptions.find((option) => option.value === this.currentFinalDecision);
			this.request.text = this.currentReasonText;
		}
	},
});
</script>

<style lang="scss" scoped>
#werberatDecisionForm {
	.werberatDecisionForm__text {
		margin-top: 2rem;

		h3 {
			margin: 0;
		}
	}

	.el-button.is-round.is-loading:disabled {
		opacity: 1;
	}

	.el-textarea {
		margin-top: 1rem;
	}
}

.has-status {
	margin-bottom: 1.5rem;
}

#dialogConfirmFinalDecision {
	.el-alert {
		background-color: var(--el-color-lightgrey-3);
	}
}
</style>
